import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { authBaseUrl } from '../../config/runtime';
import './AccessControlPanel.css';

const MODULE_LABELS = {
  dashboard: 'Dashboard',
  chamados: 'Multi atendimento',
  gestao: 'Gestao',
  projetos: 'Projetos SW',
  projetos_rma: 'Projetos RMA/HW',
  cadastros: 'Cadastros',
  knowledge: 'Base conhecimento',
  satisfacao: 'Satisfacao',
  relatorios: 'Relatorios',
  configuracoes: 'Configuracoes',
  comercial: 'CRM Comercial',
};

const DEPARTMENT_LABELS = {
  operacional: 'Operacional',
  tecnico: 'Tecnico',
  ti: 'TI',
  gestao: 'Gestao',
  comercial: 'Comercial',
};

const defaultConfig = {
  area_flags: { operacional: true, tecnico: true, ti: true, gestao: true, comercial: false },
  module_access: {
    operacional: ['dashboard', 'chamados', 'gestao', 'satisfacao'],
    tecnico: ['dashboard', 'projetos', 'projetos_rma', 'knowledge', 'relatorios'],
    ti: ['dashboard', 'projetos', 'projetos_rma', 'knowledge', 'relatorios'],
    gestao: ['dashboard', 'chamados', 'gestao', 'projetos', 'projetos_rma', 'cadastros', 'knowledge', 'satisfacao', 'relatorios'],
    comercial: ['dashboard', 'cadastros', 'relatorios', 'comercial'],
  },
};

const AccessControlPanel = () => {
  const token = localStorage.getItem('token');
  const companyCode = localStorage.getItem('companyCode') || '';
  const [config, setConfig] = useState(defaultConfig);
  const [availableModules, setAvailableModules] = useState(Object.keys(MODULE_LABELS));
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      ...(companyCode ? { 'X-Company-Code': companyCode } : {}),
    }),
    [token, companyCode]
  );

  const loadConfig = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${authBaseUrl}/access/control`, { headers });
      setConfig({
        area_flags: { ...defaultConfig.area_flags, ...(response.data?.area_flags || {}) },
        module_access: { ...defaultConfig.module_access, ...(response.data?.module_access || {}) },
      });
      const modules = Array.isArray(response.data?.available_modules) ? response.data.available_modules : [];
      setAvailableModules(modules.length ? modules : Object.keys(MODULE_LABELS));
    } catch (err) {
      setError(err?.response?.data?.message || 'Falha ao carregar painel de controle.');
    } finally {
      setLoading(false);
    }
  }, [headers, token]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const toggleArea = (area) => {
    setConfig((prev) => ({
      ...prev,
      area_flags: {
        ...prev.area_flags,
        [area]: !prev.area_flags?.[area],
      },
    }));
  };

  const toggleModule = (department, moduleId) => {
    setConfig((prev) => {
      const current = new Set(prev.module_access?.[department] || []);
      if (current.has(moduleId)) {
        current.delete(moduleId);
      } else {
        current.add(moduleId);
      }
      if (!current.has('dashboard')) {
        current.add('dashboard');
      }
      return {
        ...prev,
        module_access: {
          ...prev.module_access,
          [department]: Array.from(current),
        },
      };
    });
  };

  const handleSave = async () => {
    if (!token) return;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const response = await axios.put(`${authBaseUrl}/access/control`, config, { headers });
      setConfig({
        area_flags: { ...defaultConfig.area_flags, ...(response.data?.area_flags || {}) },
        module_access: { ...defaultConfig.module_access, ...(response.data?.module_access || {}) },
      });
      setMessage(response.data?.message || 'Painel salvo.');
    } catch (err) {
      setError(err?.response?.data?.message || 'Nao foi possivel salvar o painel.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="access-control-panel">
      <div className="access-control-panel__header">
        <h2>Painel de Controle</h2>
        <p>Defina areas da empresa e modulos liberados por setor. A area comercial pode ficar em construcao.</p>
      </div>

      {error && <p className="error-message">{error}</p>}
      {message && <p className="success-message">{message}</p>}
      {loading && <p>Carregando configuracoes...</p>}

      {!loading && (
        <>
          <section className="access-control-card">
            <div className="access-control-card__head">
              <h3>Areas da Empresa</h3>
              <small>Quando a area estiver desativada, usuarios do setor perdem acesso aos modulos dela.</small>
            </div>
            <div className="access-control-area-grid">
              {Object.keys(DEPARTMENT_LABELS).map((area) => (
                <label key={area} className="access-control-toggle">
                  <input
                    type="checkbox"
                    checked={Boolean(config.area_flags?.[area])}
                    onChange={() => toggleArea(area)}
                  />
                  <span>{DEPARTMENT_LABELS[area]}</span>
                </label>
              ))}
            </div>
            <div className="access-control-hint">Comercial: exibimos tela de CRM em construcao para preparar a frente comercial.</div>
          </section>

          <section className="access-control-card">
            <div className="access-control-card__head">
              <h3>Modulos por Setor</h3>
              <small>Administradores e master continuam com acesso completo.</small>
            </div>
            <div className="access-control-modules">
              {Object.keys(DEPARTMENT_LABELS).map((department) => (
                <article key={department} className="access-control-department">
                  <h4>{DEPARTMENT_LABELS[department]}</h4>
                  <div className="access-control-modules-grid">
                    {availableModules.map((moduleId) => {
                      const checked = (config.module_access?.[department] || []).includes(moduleId);
                      return (
                        <label key={`${department}-${moduleId}`} className="access-control-check">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleModule(department, moduleId)}
                            disabled={moduleId === 'dashboard'}
                          />
                          <span>{MODULE_LABELS[moduleId] || moduleId}</span>
                        </label>
                      );
                    })}
                  </div>
                </article>
              ))}
            </div>
          </section>

          <div className="access-control-panel__actions">
            <button className="ghost" onClick={loadConfig} disabled={saving}>Recarregar</button>
            <button onClick={handleSave} disabled={saving}>{saving ? 'Salvando...' : 'Salvar painel'}</button>
          </div>
        </>
      )}
    </div>
  );
};

export default AccessControlPanel;
